import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm } from "../note-components";
import type { UnitNotes } from "../types";

const calculusBcUnit10Notes: UnitNotes = {
  unitId: "unit-10",
  unitName: "Unit 10: Infinite Sequences and Series",
  examWeight: "17–18%",
  focus: "Sequences, Series, and Taylor/Maclaurin",
  sections: [
    {
      id: "unit-10-overview",
      title: "Overview",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            This unit introduces infinite sequences and series, including convergence tests, power series, and Taylor/Maclaurin series. This is one of the most significant BC-only topics and accounts for 17-18% of the BC exam.
          </p>
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
            <p className="text-sm text-purple-800"><strong>BC Only:</strong> This entire unit is exclusive to AP Calculus BC.</p>
          </div>
        </div>
      )
    },
    {
      id: "topic-10-1",
      title: "Topic 10.1: Defining Convergent and Divergent Infinite Series",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Sequences vs. Series</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Sequence (<InlineMath math="a_n" />):</strong> An ordered list of numbers defined by a rule (e.g., <InlineMath math="\{1, \frac{1}{2}, \frac{1}{3}, \dots\}" />). Converges if <InlineMath math="\lim_{n \to \infty} a_n = L" /></li>
              <li><strong>Infinite Series (<InlineMath math="\sum a_n" />):</strong> The sum of an infinite sequence (e.g., <InlineMath math="1 + \frac{1}{2} + \frac{1}{3} + \dots" />)</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Sequence of Partial Sums</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To determine if an infinite sum adds up to a finite number, we look at the <strong>Sequence of Partial Sums (<InlineMath math="S_n" />)</strong>:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><InlineMath math="S_1 = a_1" /></li>
              <li><InlineMath math="S_2 = a_1 + a_2" /></li>
              <li><InlineMath math="S_n = \sum_{i=1}^{n} a_i" /></li>
            </ul>
          </section>

          <KeyTerm 
            term="Convergence" 
            definition="An infinite series converges to a sum S if and only if the limit of its partial sums exists and equals S."
          />
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <BlockMath math="\lim_{n \to \infty} S_n = S" />
          </div>
        </div>
      )
    },
    {
      id: "topic-10-2",
      title: "Topic 10.2: Working with Geometric Series",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            A geometric series has a constant ratio between consecutive terms:
          </p>
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
            <BlockMath math="\sum_{n=0}^{\infty} ar^n = a + ar + ar^2 + \dots" />
            <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
              <li><InlineMath math="a" />: The first term</li>
              <li><InlineMath math="r" />: The common ratio (<InlineMath math="\frac{a_{n+1}}{a_n}" />)</li>
            </ul>
          </div>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Convergence Test</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Converges</strong> if <InlineMath math="|r| < 1" /></li>
              <li><strong>Diverges</strong> if <InlineMath math="|r| \geq 1" /></li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Finding the Sum</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If the series converges:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <BlockMath math="S = \frac{a}{1 - r}" />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-10-3",
      title: "Topic 10.3: The nth Term Test for Divergence",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            This is the <strong>first test</strong> you should perform on any general series. It describes a necessary condition for convergence.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Theorem</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <p className="text-slate-700">If <InlineMath math="\lim_{n \to \infty} a_n \neq 0" />, then <InlineMath math="\sum_{n=1}^{\infty} a_n" /> <strong>diverges</strong>.</p>
            </div>
          </section>

          <ImportantNote>
            <strong>Crucial Warning (The "Trap"):</strong> If <InlineMath math="\lim_{n \to \infty} a_n = 0" />, the test is <strong>INCONCLUSIVE</strong>. The series might converge or diverge.
            <ul className="list-disc pl-5 mt-2">
              <li><InlineMath math="\sum \frac{1}{n}" />: Terms → 0, but diverges (Harmonic)</li>
              <li><InlineMath math="\sum \frac{1}{n^2}" />: Terms → 0, and converges</li>
            </ul>
          </ImportantNote>
        </div>
      )
    },
    {
      id: "topic-10-4",
      title: "Topic 10.4: Integral Test for Convergence",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            This test relates the convergence of a series to the convergence of an improper integral.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Conditions</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Let <InlineMath math="f(x)" /> be a function such that <InlineMath math="a_n = f(n)" />. The function must be:
            </p>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700">
              <li><strong>Positive</strong> (<InlineMath math="f(x) > 0" />)</li>
              <li><strong>Continuous</strong></li>
              <li><strong>Decreasing</strong> (eventually) on <InlineMath math="[1, \infty)" /></li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Test</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>If <InlineMath math="\int_{1}^{\infty} f(x) \, dx" /> <strong>converges</strong>, then <InlineMath math="\sum_{n=1}^{\infty} a_n" /> <strong>converges</strong></li>
              <li>If <InlineMath math="\int_{1}^{\infty} f(x) \, dx" /> <strong>diverges</strong>, then <InlineMath math="\sum_{n=1}^{\infty} a_n" /> <strong>diverges</strong></li>
            </ul>
            <ImportantNote>
              The value of the integral is <strong>not</strong> the value of the sum. It simply tells you if the sum exists.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-10-5",
      title: "Topic 10.5: Harmonic Series and p-Series",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            These are specific series frequently used as benchmarks for comparison tests.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">p-Series</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="\sum_{n=1}^{\infty} \frac{1}{n^p} = \frac{1}{1^p} + \frac{1}{2^p} + \frac{1}{3^p} + \dots" />
            </div>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><strong>Converges</strong> if <InlineMath math="p > 1" /></li>
              <li><strong>Diverges</strong> if <InlineMath math="p \leq 1" /></li>
            </ul>
          </section>

          <KeyTerm 
            term="Harmonic Series" 
            definition="The specific p-series where p = 1. Always diverges even though terms approach 0."
          />
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <BlockMath math="\sum_{n=1}^{\infty} \frac{1}{n} = 1 + \frac{1}{2} + \frac{1}{3} + \dots \quad \text{DIVERGES}" />
          </div>
        </div>
      )
    },
    {
      id: "topic-10-6",
      title: "Topic 10.6: Comparison Tests for Convergence",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            When a series looks similar to a known geometric or p-series, use these tests.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Direct Comparison Test (DCT)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Let <InlineMath math="0 < a_n \leq b_n" /> for all <InlineMath math="n" />.
            </p>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700">
              <li>If the larger series <InlineMath math="\sum b_n" /> <strong>converges</strong>, the smaller series <InlineMath math="\sum a_n" /> <strong>converges</strong></li>
              <li>If the smaller series <InlineMath math="\sum a_n" /> <strong>diverges</strong>, the larger series <InlineMath math="\sum b_n" /> <strong>diverges</strong></li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Limit Comparison Test (LCT)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Given two positive series <InlineMath math="\sum a_n" /> and <InlineMath math="\sum b_n" />, evaluate:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="L = \lim_{n \to \infty} \frac{a_n}{b_n}" />
            </div>
            <p className="text-slate-700">
              If <InlineMath math="0 < L < \infty" /> (a finite, positive number), then <strong>both series behave the same</strong> (both converge or both diverge).
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-10-7",
      title: "Topic 10.7: Alternating Series Test (AST)",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            Used for series where terms alternate signs, typically containing <InlineMath math="(-1)^n" /> or <InlineMath math="\cos(n\pi)" />.
          </p>
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
            <p className="text-slate-700">Form: <InlineMath math="\sum_{n=1}^{\infty} (-1)^n a_n" /> where <InlineMath math="a_n > 0" /></p>
          </div>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Test</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The alternating series <strong>converges</strong> if two conditions are met:
            </p>
            <ol className="list-decimal pl-6 space-y-2 text-slate-700">
              <li><strong>Limit is 0:</strong> <InlineMath math="\lim_{n \to \infty} a_n = 0" /></li>
              <li><strong>Decreasing:</strong> <InlineMath math="|a_{n+1}| \leq |a_n|" /> for all <InlineMath math="n" /> (terms get smaller in magnitude)</li>
            </ol>
            <ImportantNote>
              If the limit is not 0, the series diverges by the nth Term Test, not the AST.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-10-8",
      title: "Topic 10.8: Ratio Test for Convergence",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            This is the most powerful test for series involving <strong>factorials (<InlineMath math="n!" />)</strong> or <strong>exponentials (<InlineMath math="c^n" />)</strong>.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Test</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Evaluate the limit:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="L = \lim_{n \to \infty} \left| \frac{a_{n+1}}{a_n} \right|" />
            </div>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li>If <InlineMath math="L < 1" />, the series <strong>converges</strong></li>
              <li>If <InlineMath math="L > 1" /> (or <InlineMath math="\infty" />), the series <strong>diverges</strong></li>
              <li>If <InlineMath math="L = 1" />, the test is <strong>inconclusive</strong></li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-10-9",
      title: "Topic 10.9: Determining Absolute or Conditional Convergence",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            Sometimes a series converges only because of the alternating signs (cancellation).
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Definitions</h3>
            <ol className="list-decimal pl-6 space-y-3 text-slate-700">
              <li><strong>Absolutely Convergent:</strong> The series <InlineMath math="\sum |a_n|" /> converges. (If the absolute value converges, the original series automatically converges)</li>
              <li><strong>Conditionally Convergent:</strong> The series <InlineMath math="\sum a_n" /> converges, BUT <InlineMath math="\sum |a_n|" /> diverges</li>
            </ol>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Classic Example</h3>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <p className="text-slate-700"><InlineMath math="\sum \frac{(-1)^n}{n}" /> (Alternating Harmonic Series) is <strong>Conditionally Convergent</strong>:</p>
              <ul className="list-disc pl-5 mt-2 text-sm text-slate-700">
                <li>It converges by AST</li>
                <li>However, taking the absolute value gives <InlineMath math="\sum \frac{1}{n}" />, which diverges (p-series, p=1)</li>
              </ul>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-10-10",
      title: "Topic 10.10: Alternating Series Error Bound",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            If a series converges by the Alternating Series Test, the sum <InlineMath math="S" /> can be approximated by the <InlineMath math="n" />th partial sum <InlineMath math="S_n" />. The error (remainder) is bounded by the <strong>first omitted term</strong>.
          </p>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
            <BlockMath math="|S - S_n| = |R_n| \leq |a_{n+1}|" />
          </div>

          <p className="text-slate-700">
            <strong>Logic:</strong> Since the terms alternate and shrink, the true sum is always trapped between two consecutive partial sums. The error cannot exceed the size of the next term you didn't add.
          </p>
        </div>
      )
    },
    {
      id: "topic-10-11",
      title: "Topic 10.11: Finding Taylor Polynomial Approximations of Functions",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            A Taylor Polynomial approximates a non-polynomial function near a specific center <InlineMath math="x=c" />.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Formula</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The <InlineMath math="n" />th degree Taylor Polynomial <InlineMath math="P_n(x)" /> for <InlineMath math="f" /> centered at <InlineMath math="x=c" />:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="P_n(x) = f(c) + f'(c)(x-c) + \frac{f''(c)}{2!}(x-c)^2 + \dots + \frac{f^{(n)}(c)}{n!}(x-c)^n" />
            </div>
          </section>

          <KeyTerm 
            term="Maclaurin Polynomial" 
            definition="A Taylor Polynomial centered at c = 0."
          />
        </div>
      )
    },
    {
      id: "topic-10-12",
      title: "Topic 10.12: Lagrange Error Bound",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            When we use a Taylor Polynomial <InlineMath math="P_n(x)" /> to approximate a function <InlineMath math="f(x)" />, there is an error (Remainder <InlineMath math="R_n(x)" />).
          </p>
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
            <BlockMath math="f(x) = P_n(x) + R_n(x)" />
          </div>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Lagrange Error Formula</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="|R_n(x)| \leq \frac{M}{(n+1)!}|x-c|^{n+1}" />
            </div>
            <ul className="list-disc pl-6 space-y-2 text-slate-700">
              <li><InlineMath math="n" />: The degree of the polynomial used</li>
              <li><InlineMath math="c" />: The center of the polynomial</li>
              <li><InlineMath math="x" />: The value being approximated</li>
              <li><InlineMath math="M" />: The maximum value of <InlineMath math="|f^{(n+1)}(z)|" /> for all <InlineMath math="z" /> between <InlineMath math="x" /> and <InlineMath math="c" /></li>
            </ul>
          </section>
        </div>
      )
    },
    {
      id: "topic-10-13",
      title: "Topic 10.13: Radius and Interval of Convergence of Power Series",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            A Power Series is a function of <InlineMath math="x" /> defined by:
          </p>
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
            <BlockMath math="\sum_{n=0}^{\infty} c_n (x-c)^n" />
          </div>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Finding the Radius (R) and Interval</h3>
            <ol className="list-decimal pl-6 space-y-3 text-slate-700">
              <li><strong>Apply the Ratio Test:</strong>
                <BlockMath math="\lim_{n \to \infty} \left| \frac{c_{n+1}(x-c)^{n+1}}{c_n(x-c)^n} \right| < 1" />
              </li>
              <li><strong>Solve for <InlineMath math="|x-c| < R" />:</strong> The value <InlineMath math="R" /> is the <strong>Radius of Convergence</strong>. This gives the open interval <InlineMath math="(c-R, c+R)" /></li>
              <li><strong>Check Endpoints:</strong> Plug endpoints back into the original series and use a convergence test</li>
            </ol>
          </section>

          <ImportantNote>
            You <strong>must</strong> check both endpoints separately! The interval could be <InlineMath math="[c-R, c+R)" />, <InlineMath math="(c-R, c+R]" />, open, or closed on both ends.
          </ImportantNote>
        </div>
      )
    },
    {
      id: "topic-10-14",
      title: "Topic 10.14: Finding Taylor or Maclaurin Series for a Function",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            This extends the Taylor Polynomial to infinity:
          </p>
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
            <BlockMath math="f(x) = \sum_{n=0}^{\infty} \frac{f^{(n)}(c)}{n!} (x-c)^n" />
          </div>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Essential Maclaurin Series (MEMORIZE)</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse border border-slate-300 text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border border-slate-300 p-2 text-left">Function</th>
                    <th className="border border-slate-300 p-2 text-left">Series</th>
                    <th className="border border-slate-300 p-2 text-left">Converges</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-slate-300 p-2"><InlineMath math="e^x" /></td>
                    <td className="border border-slate-300 p-2"><InlineMath math="1 + x + \frac{x^2}{2!} + \frac{x^3}{3!} + \dots = \sum \frac{x^n}{n!}" /></td>
                    <td className="border border-slate-300 p-2">All <InlineMath math="x" /></td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border border-slate-300 p-2"><InlineMath math="\sin x" /></td>
                    <td className="border border-slate-300 p-2"><InlineMath math="x - \frac{x^3}{3!} + \frac{x^5}{5!} - \dots = \sum \frac{(-1)^n x^{2n+1}}{(2n+1)!}" /></td>
                    <td className="border border-slate-300 p-2">All <InlineMath math="x" /></td>
                  </tr>
                  <tr>
                    <td className="border border-slate-300 p-2"><InlineMath math="\cos x" /></td>
                    <td className="border border-slate-300 p-2"><InlineMath math="1 - \frac{x^2}{2!} + \frac{x^4}{4!} - \dots = \sum \frac{(-1)^n x^{2n}}{(2n)!}" /></td>
                    <td className="border border-slate-300 p-2">All <InlineMath math="x" /></td>
                  </tr>
                  <tr className="bg-slate-50">
                    <td className="border border-slate-300 p-2"><InlineMath math="\frac{1}{1-x}" /></td>
                    <td className="border border-slate-300 p-2"><InlineMath math="1 + x + x^2 + \dots = \sum x^n" /></td>
                    <td className="border border-slate-300 p-2"><InlineMath math="|x| < 1" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-10-15",
      title: "Topic 10.15: Representing Functions as Power Series",
      content: (
        <div className="space-y-6">
          <p className="text-slate-700 leading-relaxed mb-4">
            You can create new power series from known ones without starting from scratch using derivatives.
          </p>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Techniques</h3>
            <ol className="list-decimal pl-6 space-y-3 text-slate-700">
              <li><strong>Substitution:</strong> To find the series for <InlineMath math="e^{2x}" />, substitute <InlineMath math="(2x)" /> into the <InlineMath math="e^x" /> series</li>
              <li><strong>Multiplication/Division:</strong> Multiply the series by <InlineMath math="x" /> or a constant</li>
              <li><strong>Differentiation/Integration:</strong> Differentiate or integrate a power series term-by-term</li>
            </ol>
          </section>

          <ImportantNote>
            <strong>Important:</strong> The Radius of Convergence (<InlineMath math="R" />) remains the same after differentiation/integration, but the convergence at the <strong>endpoints</strong> may change. You must re-test endpoints after integrating or differentiating.
          </ImportantNote>
        </div>
      )
    }
  ]
};

export default calculusBcUnit10Notes;
